import { z } from 'zod';
import { parsedBrokerageStatementSchema } from './brokerage.schemas.js';

/**
 * Start a reconciliation from the statement itself.
 *
 * Today you key in the statement date and ending balance by hand, then tick
 * entries. Here the PDF is the input: the AI reads the header for period and
 * balances, reads the transaction table, and pre-ticks the entries that match.
 *
 * (Owned by the statement-reconciliation work — extend freely.)
 */

const ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const MONEY = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'must be a decimal string');

/**
 * Days AFTER the statement period end that are still searched for matches.
 *
 * A cheque written on the 29th and posted to the books on the 3rd of the next
 * month is the ordinary case, not an error — without a grace window it looks
 * like an unexplained difference and gets re-posted as a duplicate.
 */
export const STATEMENT_FOLLOW_ON_DAYS = 30;

/** One transaction row the AI read off the statement. */
export const statementLineSchema = z.object({
  date: ISO_DATE,
  description: z.string(),
  /** Positive = money into the account, negative = money out. */
  amount: MONEY,
  /** 0.00–1.00 confidence this row was read correctly. */
  confidence: z.number().min(0).max(1).default(1),
});
export type StatementLine = z.infer<typeof statementLineSchema>;

/** What the AI read from the statement header. */
export const statementHeaderSchema = z.object({
  statementDate: ISO_DATE.nullable(),
  periodFrom: ISO_DATE.nullable(),
  periodTo: ISO_DATE.nullable(),
  beginningBalance: MONEY.nullable(),
  endingBalance: MONEY.nullable(),
  accountMask: z.string().nullable(),
  institutionName: z.string().nullable(),
  confidence: z.number().min(0).max(1).default(0),
});
export type StatementHeader = z.infer<typeof statementHeaderSchema>;

export const parsedStatementSchema = z.object({
  header: statementHeaderSchema,
  lines: z.array(statementLineSchema),
  /** Set when the document did not look like a bank statement at all. */
  warning: z.string().nullable(),
  /**
   * Present when the document is a brokerage (investment account) statement.
   * `header` and `lines` are still filled — balances from the net asset value,
   * lines from the deposits and withdrawals — so the bank flow keeps working;
   * this carries the month's components for the posting step.
   */
  brokerage: parsedBrokerageStatementSchema.optional(),
});
export type ParsedStatement = z.infer<typeof parsedStatementSchema>;

export const parseStatementInputSchema = z.object({
  fileName: z.string().min(1).max(300),
  mimeType: z.string().min(1).max(150),
  /** base64-encoded PDF. */
  content: z.string().min(1),
});
export type ParseStatementInput = z.infer<typeof parseStatementInputSchema>;

/** A statement row paired with the journal entry it appears to be. */
export const statementMatchSchema = z.object({
  statementLineIndex: z.number().int(),
  journalEntryId: z.string().uuid().nullable(),
  /** The specific bank-side line claimed, when the match came from one. */
  journalLineId: z.string().uuid().nullable().optional(),
  entryNumber: z.string().nullable(),
  entryDate: z.string().nullable(),
  signedMovement: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  /** True when the entry falls in the follow-on window, not the period itself. */
  outsidePeriod: z.boolean(),
  reason: z.string(),
});
export type StatementMatch = z.infer<typeof statementMatchSchema>;

/**
 * An unreconciled entry on the GL account, inside the statement period or its
 * follow-on window. The worksheet renders these whether or not a statement row
 * claimed them.
 */
export const statementCandidateSchema = z.object({
  journalEntryId: z.string().uuid(),
  /**
   * The specific journal LINE this candidate represents.
   *
   * Matching is line-level, not entry-level. One entry can touch the bank
   * account more than once — a deposit recorded as $1,700 and $300 on two
   * lines arrives at the bank as two separate rows. Aggregating the entry to a
   * single $2,000 movement matches neither row, so each bank-side line stands
   * as its own candidate and several rows may resolve to the same entry.
   */
  journalLineId: z.string().uuid(),
  /** How many lines of this entry touch the bank account. >1 means split. */
  entryLineCount: z.number().int().min(1),
  entryNumber: z.string(),
  date: ISO_DATE,
  description: z.string(),
  /** Signed movement of THIS LINE on the GL account, in normal-balance terms. */
  signedMovement: MONEY,
  /** True when the entry is dated after the period end (follow-on window). */
  outsidePeriod: z.boolean(),
});
export type StatementCandidate = z.infer<typeof statementCandidateSchema>;

export const statementReconPreviewSchema = z.object({
  parsed: parsedStatementSchema,
  matches: z.array(statementMatchSchema),
  /** Entries in range that no statement row explains at all. */
  unmatchedEntryIds: z.array(z.string().uuid()),
  /**
   * Entries where SOME but not all bank-side lines were found on the statement.
   *
   * Clearing is entry-level, so ticking one of these counts the entry's whole
   * movement against a statement that only evidenced part of it. Surfaced
   * separately and never pre-ticked — it is a question, not a match.
   */
  partiallyMatchedEntryIds: z.array(z.string().uuid()).default([]),
  /** Statement rows with no entry behind them — usually genuinely missing. */
  unmatchedLineIndexes: z.array(z.number().int()),
  /** Every unreconciled entry considered, so the worksheet can show them all. */
  candidates: z.array(statementCandidateSchema),
  /** The window actually searched (period plus the follow-on days). */
  periodFrom: ISO_DATE,
  periodTo: ISO_DATE,
  followOnThrough: ISO_DATE,
  /** True when phase 2 (the AI pass) actually ran. */
  aiUsed: z.boolean(),
});
export type StatementReconPreview = z.infer<typeof statementReconPreviewSchema>;

/**
 * Ask the server to match a parsed statement against the books.
 *
 * The parsed statement round-trips through the client because the owner is
 * allowed to correct the AI's reading before anything is matched — the values
 * that come back here are the confirmed ones, not necessarily what was read.
 */
export const statementPreviewInputSchema = z.object({
  glAccountId: z.string().uuid(),
  parsed: parsedStatementSchema,
  /** Overrides the parsed header when the owner corrected the period. */
  periodFrom: ISO_DATE.optional(),
  periodTo: ISO_DATE.optional(),
});
export type StatementPreviewInput = z.infer<typeof statementPreviewInputSchema>;

export const startReconFromStatementInputSchema = z.object({
  glAccountId: z.string().uuid(),
  statementDate: ISO_DATE,
  statementBeginningBalance: MONEY,
  statementEndingBalance: MONEY,
  /** Entries to pre-clear on the new worksheet. */
  journalEntryIds: z.array(z.string().uuid()).default([]),
  /** Vault document id of a statement ALREADY in the vault, linked to the reconciliation. */
  vaultDocumentId: z.string().uuid().nullable().optional(),
  /**
   * The statement file itself, as it was dropped. Stored in the vault, filed
   * under the month it covers and linked to the new reconciliation — so the
   * evidence the AI read from is the evidence the packet carries. Without this
   * the drop-to-start flow parsed the PDF and then threw it away.
   */
  statement: parseStatementInputSchema.optional(),
});
export type StartReconFromStatementInput = z.infer<typeof startReconFromStatementInputSchema>;
